'use client'
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { UserSearchComponent } from './UserSearchComponent'
import {
  Paper,
  Tooltip,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@material-ui/core'
import { loadUsers } from '~/state/modules/users'
import { loadSeats } from '~/state/modules/planimetry'
import { sendGiftTicket } from 'state/modules/orders'
import { LocalActivity } from '@material-ui/icons'
import { ConfirmationDialog } from '~/views/shared'
import { showSnackbarError } from 'views/utils/snackbar'
import StatusLegend from '../components/StatusLegend'
import numeral from 'numeral'

export default function ControlPanel({
  selectedStatus,
  statusCounts,
  seatsSelected,
  clearSelectedSeats,
  selectAllChecked,
  onSelectAll,
  amountSelectedSeats,
  sectorId,
  ticketId,
  totalToPay,
  onSuccess,
  resetUserSearch,
  soldUser, // nuevo prop
  clearSoldUser
}) {
  const [user, setUser] = useState()
  const [isForeign, setIsForeign] = useState(false)
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const dispatch = useDispatch()
  const { pendingUsers } = useSelector((state) => state.users)
  const { pendingTicketSending } = useSelector((state) => state.orders)
  const { country } = useSelector((state) => state.app)
  const documentType = country === 'ar' ? 'DNI' : 'CI'
  const [userSearchValue, setUserSearchValue] = useState('')
  // Si viene soldUser, lo usamos para mostrar en el panel (prioridad)
  const displayedUserId = soldUser?.userId
  const displayedFullName = soldUser?.fullName
  const displayedIdentificationType = soldUser?.identificationType
  const displayedDocumentNumber = soldUser?.documentNumber

  //
  const handleForeignChange = (isForeign) => {
    setIsForeign(isForeign)
  }

  const performSearchUsers = async (isForeign, searchKey) => {
    const message = `No existen usuarios con el ${
      isForeign ? 'N° de identificación' : documentType
    } ingresado. El cliente debe registrarse en ticketPass y completar todos sus datos en su perfil.`
    if (searchKey.trim().length > 3) {
      const payload = {
        IdentificationType: isForeign ? 'OTRO' : documentType,
        IdentificationNumber: searchKey.trim(),
        SearchPattern: ''
      }
      const users = await dispatch(loadUsers(payload))
      if (users.length === 0) {
        showSnackbarError(message, 5000)
      } else {
        setUser(users[0])
      }
    }
    setUserSearchValue(searchKey)
  }

  const handleSendTicket = async () => {
    const giftTicket = {
      TicketTypeId: ticketId,
      UserIds: [user.Id],
      NotifyManager: true,
      Units: amountSelectedSeats,
      Seats: seatsSelected.map((s) => ({
        id: s.seatId
      }))
    }
    try {
      await dispatch(sendGiftTicket(giftTicket))
      setOpenSuccessDialog(true)
    } catch (error) {
      showSnackbarError(error)
    }
  }

  const handleConfirmSendTicket = async () => {
    setOpenConfirmDialog(false)
    await handleSendTicket()
  }

  const handleSuccessDialogClose = () => {
    setOpenSuccessDialog(false)
    setUser(null)
    setUserSearchValue('')
    clearSelectedSeats()
    dispatch(loadSeats(sectorId))
    if (onSuccess) onSuccess()

    if (typeof clearSoldUser === 'function') clearSoldUser()
  }

  useEffect(() => {
    if (resetUserSearch) {
      setUserSearchValue('')
      setUser(null)
    }
  }, [resetUserSearch])

  return (
    <>
      <Paper elevation={0} sx={{ mb: 3, boxShadow: 'none', border: 'none' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StatusLegend
              selectedStatus={selectedStatus}
              statusCounts={statusCounts}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: displayedUserId ? 'space-between' : 'end',
                alignItems: 'center',
                marginTop: 8,
                marginLeft: 8,
                gap: 8
              }}>
              <div
                style={{ visibility: displayedUserId ? 'visible' : 'hidden' }}>
                <Typography variant='subtitle2' style={{ fontWeight: 'bold' }}>
                  {'UserId'}:&nbsp;
                  <Typography variant='subtitle2' component='span'>
                    {displayedUserId}
                  </Typography>
                </Typography>
                <Typography variant='subtitle2' style={{ fontWeight: 'bold' }}>
                  {'Nombre'}:&nbsp;
                  <Typography variant='subtitle2' component='span'>
                    {displayedFullName}
                  </Typography>
                </Typography>
                <Typography variant='subtitle2' style={{ fontWeight: 'bold' }}>
                  {displayedIdentificationType}:&nbsp;
                  <Typography variant='subtitle2' component='span'>
                    {displayedDocumentNumber}
                  </Typography>
                </Typography>
              </div>
              <FormControlLabel
                style={{
                  display: 'flex',
                  marginRight: 0,
                  alignItems: 'center'
                }}
                control={
                  <Checkbox
                    checked={selectAllChecked}
                    onChange={onSelectAll}
                    color='primary'
                  />
                }
                label='Seleccionar todos'
              />
            </div>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              marginTop: -16
            }}>
            <UserSearchComponent
              performSearch={performSearchUsers}
              searchValue={userSearchValue}
              pending={pendingUsers}
              isForeign={isForeign}
              handleForeignChange={handleForeignChange}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between'
              }}>
              <div>
                <Typography
                  variant='h5'
                  style={{
                    display: 'flex',
                    marginLeft: 8,
                    alignItems: 'center'
                  }}>
                  Usuario:&nbsp;
                  <Typography
                    style={{
                      fontWeight: user != null && 'bold',
                      color: user != null ? 'black' : 'gray',
                      fontStyle: user != null ? 'normal' : 'italic'
                    }}>
                    {user != null ? user?.FullName : 'No Seleccionado'}
                  </Typography>
                </Typography>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 4,
                    marginLeft: 8,
                    flexWrap: 'wrap'
                  }}>
                  <Typography
                    style={{
                      fontSize: 14.5,
                      fontWeight: 'bold',
                      color: '#ba68c8'
                    }}>
                    Ubicaciones seleccionadas:
                  </Typography>
                  <Typography style={{ fontSize: 14.5, fontWeight: 'bold' }}>
                    {seatsSelected.length > 0
                      ? seatsSelected.map((s) => `${s.description}`).join(', ')
                      : '-'}
                  </Typography>
                </div>
              </div>
              <div>
                <div style={{ width: 200 }}>
                  <Typography
                    variant='h5'
                    style={{ display: 'flex', marginLeft: 11 }}>
                    Total Entradas:&nbsp;
                    <Typography style={{ fontWeight: 'bold' }}>
                      {amountSelectedSeats}
                    </Typography>
                  </Typography>
                  <Typography
                    variant='h5'
                    style={{ display: 'flex', marginLeft: 11 }}>
                    Total A Pagar:&nbsp;
                    <Typography style={{ fontWeight: 'bold' }}>
                      {numeral(totalToPay).format('$0,0[.]00')}
                    </Typography>
                  </Typography>
                </div>
                <Tooltip
                  title={
                    amountSelectedSeats !== 0 && !user
                      ? 'Seleccione el Cliente.'
                      : amountSelectedSeats === 0 && user
                        ? 'Seleccione al menos una Ubicación.'
                        : ''
                  }
                  placement='right-end'>
                  <span>
                    <Button
                      autoFocus={false}
                      variant='contained'
                      color='primary'
                      style={{ marginTop: 8, marginRight: 8, marginLeft: 12 }}
                      startIcon={<LocalActivity />}
                      disabled={
                        user == null ||
                        amountSelectedSeats === 0 ||
                        pendingTicketSending
                      }
                      onClick={() => setOpenConfirmDialog(true)}>
                      {pendingTicketSending ? (
                        <CircularProgress size={24} color='secondary' />
                      ) : (
                        'Enviar Tickets'
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </Grid>
        </Grid>
      </Paper>
      <ConfirmationDialog
        open={openConfirmDialog}
        loading={pendingTicketSending}
        handleConfirmation={handleConfirmSendTicket}
        onCancel={() => setOpenConfirmDialog(false)}
        title='¡ADVERTENCIA!'
        message='Está por enviar las ubicaciones:'
        optionalMessage={
          <span style={{ fontWeight: 'bold', marginLeft: 4 }}>
            {seatsSelected.length > 0
              ? seatsSelected.map((s) => s.description).join(', ')
              : '-'}
            {user?.FullName && (
              <span style={{ fontWeight: 'normal', marginLeft: 4 }}>
                al usuario <b>{user.FullName}</b>.
              </span>
            )}
          </span>
        }
      />
      <Dialog open={openSuccessDialog}>
        <DialogTitle>Tickets Enviados!</DialogTitle>
        <DialogActions>
          <Button
            onClick={handleSuccessDialogClose}
            color='primary'
            autoFocus={false}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
